<template>
  <UIModal
    class="ModalChatSearch"
    ariaLabel="Search chats"
    :isOpen="isOpen"
    size="lg"
    @close="emit('close')"
    @enter="emit('close')"
  >
    <template #container>
      <div class="ModalChatSearch__container">
        <UISearch
          v-model="searchString"
          class="ModalChatSearch__search"
          aria-label="Search chats"
          placeholder="Search"
          isSanitised
        />
        <UIIcon
          class="ModalChatSearch__close"
          name="close"
          @click="emit('close')"
        />

        <div class="ModalChatSearch__results">
          <RouterLink
            class="ModalChatSearch__new-chat"
            :to="{ name: 'NewChat' }"
            @click="emit('close')"
          >
            <UIIcon name="newChat" />
            New chat
          </RouterLink>

          <div v-if="noResults" class="ModalChatSearch__no-results">
            No results have been found.
          </div>

          <ChatHistory
            v-for="[label, _chats] in filteredChatGroups.entries()"
            :key="label"
            :label="label"
            :chats="_chats"
            :showContextMenu="false"
            @click="emit('close')"
          />
        </div>
      </div>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { UIModal, UISearch } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'

import type { IChat } from '@/apiClient/types/chat'
import { useChatStore } from '@/store/chat'
import { matchSearchString } from '@/utils'

import ChatHistory from '../TheSidebar/components/ChatHistory.vue'
import UIIcon from '../ui/icons/UIIcon.vue'

const emit = defineEmits(['close', 'confirm'])
const props = withDefaults(
  defineProps<{
    isLoading?: boolean
    isOpen?: boolean
  }>(),
  {
    isLoading: false,
    isOpen: false,
  },
)
const { chatsByDateGroups } = storeToRefs(useChatStore())

const searchString = ref('')

const filteredChatGroups = computed(() => {
  if (!searchString.value.trim()) {
    return chatsByDateGroups.value
  }

  const filtered = new Map<string, IChat[]>()

  for (const [label, chats] of chatsByDateGroups.value.entries()) {
    const filteredChats = chats.filter((chat) =>
      matchSearchString(chat.title, searchString.value),
    )

    if (filteredChats.length > 0) {
      filtered.set(label, filteredChats)
    }
  }

  return filtered
})
const noResults = computed(() => {
  return searchString.value.trim() && filteredChatGroups.value.size === 0
})

watch(
  () => props.isOpen,
  (newVal) => {
    if (!newVal) {
      searchString.value = ''
    }
  },
)
</script>

<style scoped lang="scss">
.ModalChatSearch {
  .ModalChatSearch__container {
    @include font-size-14px;
    height: 21.875rem;
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 2.75rem 1fr;
  }

  .ModalChatSearch__search {
    border-bottom: 1px solid var(--border-heavy);
    // Leave room for the modal's own close icon beside the field's clear control.
    box-sizing: border-box;
    padding-right: 2rem;

    :deep(.UIInputTextBase__field) {
      border: none;
      border-radius: 0;
      background: transparent;
    }
  }

  .ModalChatSearch__close {
    --size: 0.75rem;
    --color: var(--icon-secondary);
    position: absolute;
    top: 1rem;
    right: 0.75rem;
    cursor: pointer;
  }

  .ModalChatSearch__new-chat {
    display: flex;
    gap: 0.5rem;
    padding: 0.875rem 1.25rem;
    color: var(--text-primary);
    --color: var(--icon-secondary);
    cursor: pointer;
    text-decoration: none;

    &:hover {
      background-color: var(--interactive-bg-tertiary-hover);
    }

    &:active {
      background: var(--interactive-bg-tertiary-press);
    }
  }

  .ModalChatSearch__results {
    @include customScrollbar();
    // max-height: 350px;
  }

  .ModalChatSearch__no-results {
    width: 100%;
    padding: 12px;
  }
}
</style>
